import { ComponentPropsWithoutRef, useContext, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import core, { App } from '@gin-betting/core';
import filters from '@gin-betting/filters';
import { intersection } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';

import { CategorizerTournamentMetaKey } from 'app-constants';
import { useAppliedFilters, useMemoSelector } from 'hooks';
import globalMessages from 'messages';
import { Action, AnalyticAttrs, Category } from 'services/analytics';
import type { SegmentControl } from 'ui-lib';
import { stringify } from 'utils';
import { OverlayType } from '../../constants';
import messages from '../../messages';
import { hasAnyMetaEventsInCategorizer } from '../../utils';
import { MobileFiltersCtx } from '../MobileFilters/MobileFiltersCtx';

import './sportsNavigation.scss';

interface Output {
    isCompact: boolean;
    withoutRender: boolean;
    tabs: ComponentPropsWithoutRef<typeof SegmentControl>['tabs'];
}

const {
    utils: { getAppliedFilters },
    viewport: {
        selectors: { selectIsCompact },
    },
    services: { getHistory },
    hooks: { useDeepMemo },
} = core;

const {
    selectors: { selectCategorizerData },
} = filters;

const useSportsNavigation = (): Output => {
    const { formatMessage } = useIntl();
    const {
        isCompact,
        livePrematchIds: { liveSportIds, prematchSportIds },
    } = useMemoSelector(selectProps, {});

    const { pathname } = useLocation();
    const { sportId, sportIds, tournamentId } = useAppliedFilters();

    const sportIdsMem = useDeepMemo(sportIds);

    const { currentOverlay, setOverlay } = useContext(MobileFiltersCtx);

    const handleAll = (withHistoryReplace?: boolean) => {
        handleTab({
            pathname: App.Routes.UPCOMING_LIST,
            withResetFilters: isCompact,
            withHistoryReplace,
        });

        if (!isCompact) return;

        setOverlay(OverlayType.NONE);
    };

    const handleLive = (withHistoryReplace?: boolean) => {
        handleTab({
            pathname: App.Routes.LIVE_LIST,
            withResetFilters: isCompact,
            withHistoryReplace,
        });
    };

    const handleNextToGo = () => {
        handleTab({
            pathname: App.Routes.PREMATCH,
            withResetFilters: isCompact,
        });
    };

    const handleTournaments = () => {
        if (!isCompact) return;

        setOverlay(OverlayType.TOURNAMENT_LIST);
        handleTab({
            pathname: App.Routes.UPCOMING_LIST,
            withResetFilters: true,
        });
    };

    const isLiveTabActive = pathname === App.Routes.LIVE_LIST;

    const isNextToActive = pathname === App.Routes.PREMATCH;

    const isAllTabActive =
        pathname === App.Routes.UPCOMING_LIST &&
        (isCompact
            ? !tournamentId && currentOverlay === OverlayType.NONE
            : !isLiveTabActive && !isNextToActive);

    const isTournamentTabActive =
        pathname === App.Routes.UPCOMING_LIST && !isAllTabActive;

    const redirectInfo = {
        liveSportIds,
        handleLive,
        handleAll,
        isLiveTabActive,
        isAllTabActive,
    };

    const redirectInfoRef = useRef(redirectInfo);
    redirectInfoRef.current = redirectInfo;

    useEffect(() => {
        const refValue = redirectInfoRef.current;

        const sIds: string[] = [
            ...(sportId ? [sportId] : []),
            ...(sportIdsMem || []),
        ];

        const isLiveSports =
            isCompact && intersection(sIds, refValue.liveSportIds).length > 0;

        if (isLiveSports) {
            if (refValue.isLiveTabActive) return;

            refValue.handleLive(true);
        } else if (!refValue.isAllTabActive && sIds.length > 0) {
            refValue.handleAll(true);
        }
    }, [sportId, sportIdsMem, isCompact]);

    const tabs: Output['tabs'] = [
        {
            text: formatMessage(globalMessages.all),
            onClick: handleAll,
            isActive: isAllTabActive,
        },
        {
            text: formatMessage(globalMessages.live),
            onClick: handleLive,
            isActive: isLiveTabActive,
            analyticAttrs: ANALYTICS_LIVE,
        },
        {
            text: formatMessage(globalMessages.nextToGo),
            onClick: handleNextToGo,
            isActive: isNextToActive,
            analyticAttrs: ANALYTICS_PREMATCH,
        },
    ];

    if (isCompact && !!sportId) {
        tabs.push({
            text: formatMessage(messages.tournaments),
            onClick: handleTournaments,
            isActive: isTournamentTabActive,
        });
    }

    const withoutRender =
        !isCompact &&
        !!sportIds?.length &&
        [liveSportIds, prematchSportIds].some(
            ids => intersection(ids, sportIds).length === 0
        );

    return {
        tabs,
        withoutRender,
        isCompact,
    };
};

interface Options {
    pathname: App.Routes;
    withResetFilters: boolean;
    withHistoryReplace?: boolean;
}

function handleTab({
    pathname,
    withResetFilters,
    withHistoryReplace,
}: Options) {
    const history = getHistory();

    const nextLocation = {
        ...history.location,
        pathname,
    };

    if (withResetFilters) {
        const { sportId, sportIds } = getAppliedFilters();

        nextLocation.search = stringify({
            sportId,
            sportIds,
        });
    }

    if (withHistoryReplace) {
        history.replace(nextLocation);

        return;
    }

    history.push(nextLocation);
}

interface LivePrematchIds {
    liveSportIds: string[];
    prematchSportIds: string[];
}

const selectLivePrematchIds = createSelector(
    selectCategorizerData,
    (categorizerItems): LivePrematchIds =>
        categorizerItems.reduce(
            (acc: LivePrematchIds, { meta, value: sportId }) => {
                const isLive = hasAnyMetaEventsInCategorizer(
                    meta,
                    CategorizerTournamentMetaKey.LiveEventsCount
                );

                const isPrematch = hasAnyMetaEventsInCategorizer(
                    meta,
                    CategorizerTournamentMetaKey.PrematchEventsCount
                );

                if (isLive) {
                    acc.liveSportIds.push(sportId);
                }

                if (isPrematch) {
                    acc.prematchSportIds.push(sportId);
                }

                return acc;
            },
            {
                liveSportIds: [],
                prematchSportIds: [],
            }
        )
);

const selectProps = createStructuredSelector({
    isCompact: selectIsCompact,
    livePrematchIds: selectLivePrematchIds,
});

const ANALYTICS_LIVE: AnalyticAttrs = {
    'data-category': Category.FiltersLive,
    'data-action': Action.Open,
};

const ANALYTICS_PREMATCH: AnalyticAttrs = {
    'data-category': Category.FiltersNextToGo,
    'data-action': Action.Open,
};

export default useSportsNavigation;
